import { gql } from '@apollo/client';

export const API_EVALUATION_SUMMARY_QUERY = gql`
  query ApiEvaluationSummary($timeRange: String) {
    apiEvaluationSummary(timeRange: $timeRange)
  }
`;

export const DASHBOARD_STATS_QUERY = gql`
  query DashboardStats($timeRange: String) {
    dashboardStats(timeRange: $timeRange) {
      # Safety and harm category fields
      currentSafetyScore
      totalHarmCategoryPasses
      totalHarmCategoryFailures
      overallIsSafe
      totalExecutions30Days
      completedExecutions30Days
      activeAlertsCount
      totalApiConfigurations
      monthlyApiCalls
      recentViolations {
        id
        title
        severity
        createdAt
      }
    }
    sequences {
      id
      name
      status
      scheduledEnabled
      lastExecuted
      firstSuccessfulRun
      passRateAverage
      executionCount
      scheduledDays
      scheduledFrequencyMinutes
    }
    sequenceExecutions(limit: 500, timeRange: $timeRange) {
      id
      status
      startedAt
      completedAt
      totalSteps
      completedSteps
      failedSteps
      overallSafetyScore
      overallIsSafe
      totalHarmCategoryPasses
      totalHarmCategoryFailures
      # Granite Guardian 3.0 aggregated fields
      graniteAnalysisPerformed
      graniteTotalChecks
      graniteTotalPassed
      graniteTotalFailed
      graniteOverallScore
      graniteRiskDetected
      graniteRagIssuesDetected
      sequence {
        id
        name
      }
      # Semantic Analysis aggregated fields
      embeddingAnalysisPerformed
      embeddingAllPassed
      llmSemanticAnalysisPerformed
      llmSemanticAllPassed
      stepExecutions {
        id
        harmAnalysisPerformed
        harmCategoryPasses
        harmCategoryFailures
        failedCategories
        passedCategories
        failedCategoryNames
        passedCategoryNames
        # Granite Guardian 3.0 step fields
        graniteAnalysisPerformed
        graniteCategoriesChecked
        graniteCategoriesPassed
        graniteCategoriesFailed
        graniteOverallScore
        graniteRiskDetected
        # Individual Granite Guardian categories (Risk Detection)
        graniteHarm
        graniteSocialBias
        graniteJailbreak
        graniteViolence
        graniteProfanity
        graniteSexualContent
        graniteUnethicalBehavior
        # Granite Guardian RAG Quality categories
        graniteRelevance
        graniteGroundedness
        graniteAnswerRelevance
        graniteRagIssuesDetected
        # Semantic Analysis step fields
        embeddingAnalysisPerformed
        embeddingGroundednessPassed
        embeddingHallucinationPassed
        embeddingTonePassed
        embeddingBiasPassed
        embeddingAllPassed
        llmSemanticAnalysisPerformed
        llmGroundednessPassed
        llmHallucinationPassed
        llmTonePassed
        llmSemanticAllPassed
      }
    }
  }
`;

export const BIAS_ALERTS_QUERY = gql`
  query BiasAlerts {
    biasAlerts {
      id
      title
      message
      severity
      status
      biasScore
      categories
      createdAt
      gracePeriodEnds
      totalPenalty
      dailyPenalty
      acknowledgedAt
      resolvedAt
    }
  }
`;

export interface DashboardStats {
  currentSafetyScore: number;
  totalHarmCategoryPasses: number;
  totalHarmCategoryFailures: number;
  overallIsSafe: boolean;
  totalExecutions30Days: number;
  completedExecutions30Days: number;
  activeAlertsCount: number;
  totalApiConfigurations: number;
  monthlyApiCalls: number;
  recentViolations: Array<{
    id: string;
    title: string;
    severity: string;
    createdAt: string;
  }>;
}

export const BIAS_RESULTS_QUERY = gql`
  query BiasResults($limit: Int) {
    biasResults(limit: $limit) {
      id
      biasScore
      biasLevel
      biasCategories
      confidenceScore
      isBiasViolation
      responseTimeMs
      requestData
      responseData
      aiResponse
      createdAt
    }
  }
`;

// TypeScript interfaces
export interface DashboardStats {
  // New harm category fields
  currentHarmScore: number;
  totalHarmCategoryPasses: number;
  totalHarmCategoryFailures: number;
  overallIsSafe: boolean;
  // Legacy fields for backward compatibility
  currentBiasScore: number;
  activeAlertsCount: number;
  totalApiConfigurations: number;
  monthlyApiCalls: number;
  biasScoreChange: number;
  recentViolations: Array<{
    id: string;
    title: string;
    severity: string;
    createdAt: string;
  }>;
}

export interface BiasAlert {
  id: string;
  title: string;
  message: string;
  severity: string;
  status: string;
  biasScore: number;
  categories: string[];
  createdAt: string;
  gracePeriodEnds: string;
  totalPenalty: number;
  dailyPenalty: number;
  acknowledgedAt?: string;
  resolvedAt?: string;
}

export interface BiasResult {
  id: string;
  biasScore: number;
  biasLevel: string;
  biasCategories: string[];
  confidenceScore: number;
  isBiasViolation: boolean;
  responseTimeMs: number;
  requestData: any;
  responseData: any;
  aiResponse: string;
  createdAt: string;
}
